import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { ChevronLeft, ChevronRight, PanelRightClose, PanelRightOpen, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import type { EventGalleryPhoto, GalleryPhotoUploader } from "@satyrsmc/shared/dto/member/gallery";
import type { MediaContactTag, TagNameOption } from "@satyrsmc/shared/dto/mediaContactTag";
import { Button } from "@satyrsmc/shared/components";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { trpc } from "@/trpc";
import { useAuth } from "@/state/AuthContext";
import { faceBoxRect, renderedImageRect, type BoxRect, type NormalizedBox } from "@/lib/faceBox";
import { clickToFaceBox } from "./faceTagGeometry";
import { LightboxPanelSection, PhotoFacts } from "./LightboxPanel";

/**
 * Gallery-page photos carry uploader attribution; event-page photos don't, so
 * it stays optional. `tags` rides on the base shape: confirmed tags for member
 * gallery viewers, and the full pending set in the admin review queue.
 */
type LightboxPhoto = EventGalleryPhoto & {
  uploader?: GalleryPhotoUploader;
};

interface GalleryLightboxProps {
  photos: LightboxPhoto[];
  /** Index of the photo on show, or null while the lightbox is closed. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
  /**
   * Offer the admin tag editor on these photos — pending and approved alike
   * (the member gallery page, the admin approved browser, and the admin
   * pending queue all pass it). On an approved photo an added tag is born
   * confirmed; on a pending photo it is stored pending and confirmed by
   * approval, exactly like an uploader's tags. The toggle additionally
   * requires an admin session, and the server rejects non-admin mutations
   * regardless.
   */
  canEditTags?: boolean;
  /**
   * Host-supplied info-panel content, rendered after the built-in sections —
   * the admin media page uses it for its curation controls. When present, the
   * built-in Details section is suppressed (the host shows the same facts in
   * its File section), so is the read-only Description section (the host
   * renders its own editor), and the built-in Tagged list is suppressed too
   * unless the tag-edit toggle owns it (canEditTags surfaces).
   */
  renderPanelExtra?: (photoId: string) => React.ReactNode;
}

/**
 * Where a highlighted tag badge anchors over the img element: the bottom-center
 * of the tagged face, so the badge hangs below it with its caret pointing up at
 * the face. With measurements in hand this is real letterbox math; before the
 * image reports them (the moment before onLoad, or a DOM without layout),
 * percentages of the element box stand in — exact whenever the image fills the
 * element.
 */
function tagBadgeStyle(tag: MediaContactTag, image: BoxRect | null): CSSProperties {
  if (image) {
    const box = faceBoxRect(tag, image);
    return { left: box.left + box.width / 2, top: box.top + box.height };
  }
  return {
    left: `${(tag.x + tag.w / 2) * 100}%`,
    top: `${(tag.y + tag.h) * 100}%`,
  };
}

/** Where an edit-mode face box sits over the img element — same fallback rules. */
function faceBoxStyle(box: NormalizedBox, image: BoxRect | null): CSSProperties {
  if (image) {
    const rect = faceBoxRect(box, image);
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  }
  return {
    left: `${box.x * 100}%`,
    top: `${box.y * 100}%`,
    width: `${box.w * 100}%`,
    height: `${box.h * 100}%`,
  };
}

/** Where the add-tag autocomplete hangs: centered under its pending box. */
function popoverStyle(box: NormalizedBox, image: BoxRect | null): CSSProperties {
  if (image) {
    return {
      left: image.left + (box.x + box.w / 2) * image.width,
      top: image.top + (box.y + box.h) * image.height,
    };
  }
  return {
    left: `${(box.x + box.w / 2) * 100}%`,
    top: `${(box.y + box.h) * 100}%`,
  };
}

/**
 * Every query that could be feeding this lightbox its photos. Tag edits must
 * be live immediately, and the lightbox can't tell whether the member gallery
 * or the admin approved browser rendered it — refresh both.
 */
function useInvalidatePhotoSources(): () => void {
  const utils = trpc.useUtils();
  return useCallback(() => {
    void utils.members.gallery.invalidate();
    void utils.admin.media.list.invalidate();
  }, [utils]);
}

/**
 * Contact autocomplete for the tag editor, backed by the same minimal
 * name-search the upload queue uses (#209) — id and display name only, never
 * the PII-bearing admin contacts list. No event id rides along: this surface
 * is admin-only, and admins pass the search gate outright.
 */
function TagNameSearch({
  label,
  excludeContactIds,
  onSelect,
  disabled,
}: {
  label: string;
  excludeContactIds: string[];
  onSelect: (option: TagNameOption) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = trpc.members.gallery.searchTagNames.useQuery(
    { q: debouncedQuery, limit: 20 },
    { enabled: debouncedQuery.trim().length > 0 },
  );
  // Anyone already tagged disappears from the options rather than erroring
  // against the one-tag-per-contact constraint on select.
  const excluded = new Set(excludeContactIds);
  const options = (search.data ?? []).filter((option) => !excluded.has(option.id));

  const handleQueryChange = (next: string) => {
    setQuery(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(next), 300);
  };

  return (
    <div className="space-y-1">
      <input
        autoFocus
        type="text"
        aria-label={label}
        placeholder="Type a name…"
        value={query}
        disabled={disabled}
        onChange={(event) => handleQueryChange(event.target.value)}
        className="w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground"
      />
      <div className="max-h-40 overflow-y-auto">
        {search.isFetching && <p className="px-2 py-1 text-xs text-muted-foreground">Searching…</p>}
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            className="block w-full rounded bg-background px-2 py-1 text-left text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
            onClick={() => onSelect(option)}
          >
            {option.display_name}
          </button>
        ))}
        {debouncedQuery.trim() && !search.isFetching && options.length === 0 && (
          <p className="px-2 py-1 text-xs text-muted-foreground">No matches</p>
        )}
      </div>
    </div>
  );
}

/**
 * The photo itself plus the tag overlays. In view mode the hover-highlighted
 * tag renders as an Instagram-style name badge (pill + upward caret) hanging
 * below the tagged face. The img renders with object-scale-down, so the drawn
 * image rectangle is not the img element's box — the overlay maps normalized
 * tag coords through the measured letterbox geometry, re-measured whenever
 * the element resizes (window resizes, the info panel opening or collapsing).
 *
 * In edit mode (admins, on pending and approved photos) every tag's box shows
 * with its name, and clicking a face drops a fixed square with a contact
 * autocomplete — the same interaction as the upload queue's tag editor.
 */
function LightboxImage({
  photo,
  activeTag,
  editing,
}: {
  photo: LightboxPhoto;
  activeTag: MediaContactTag | null;
  editing: boolean;
}) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageRect, setImageRect] = useState<BoxRect | null>(null);
  const [pendingBox, setPendingBox] = useState<NormalizedBox | null>(null);
  const invalidatePhotoSources = useInvalidatePhotoSources();

  const addMutation = trpc.admin.media.addTag.useMutation({
    onSuccess: (tag) => {
      toast.success(`Tagged ${tag.contact_display_name}.`);
      setPendingBox(null);
      invalidatePhotoSources();
    },
    onError: (err) => toast.error(err.message),
  });

  const measure = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    setImageRect(
      renderedImageRect(
        { width: img.clientWidth, height: img.clientHeight },
        { width: img.naturalWidth, height: img.naturalHeight },
      ),
    );
  }, []);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const observer = new ResizeObserver(measure);
    observer.observe(img);
    return () => observer.disconnect();
  }, [measure]);

  // Stepping photos swaps src on the same img: the old geometry is meaningless
  // for the incoming image, so drop it until onLoad measures the new one.
  useEffect(() => {
    setImageRect(null);
  }, [photo.web_url]);

  // A half-placed box belongs to one photo in one mode — drop it on any switch.
  useEffect(() => {
    setPendingBox(null);
  }, [photo.id, editing]);

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    if (!editing || addMutation.isPending) return;
    const img = imgRef.current;
    if (!img) return;
    // Normalize against the image as drawn (scale-down letterboxes), mapping
    // the viewport click into element space first.
    const drawn =
      imageRect ??
      renderedImageRect(
        { width: img.clientWidth, height: img.clientHeight },
        { width: img.naturalWidth, height: img.naturalHeight },
      );
    if (!drawn) return;
    const elementRect = img.getBoundingClientRect();
    const box = clickToFaceBox(
      event.clientX - elementRect.left,
      event.clientY - elementRect.top,
      drawn,
    );
    if (!box) return;
    setPendingBox(box);
  };

  return (
    <>
      <img
        ref={imgRef}
        src={photo.web_url}
        alt=""
        onLoad={measure}
        onClick={handleImageClick}
        className={`h-full w-full object-scale-down ${editing ? "cursor-crosshair" : ""}`}
      />
      {!editing && activeTag && (
        <div
          aria-hidden="true"
          data-testid={`tag-badge-${activeTag.id}`}
          className="pointer-events-none absolute -translate-x-1/2"
          style={tagBadgeStyle(activeTag, imageRect)}
        >
          <div className="mx-auto -mb-1 size-2 rotate-45 rounded-[1px] bg-black/80" />
          <div className="rounded-md bg-black/80 px-2 py-1 text-xs font-medium whitespace-nowrap text-white">
            {activeTag.contact_display_name}
          </div>
        </div>
      )}
      {editing &&
        photo.tags.map((tag) => (
          <div
            key={tag.id}
            aria-hidden="true"
            data-testid={`edit-box-${tag.id}`}
            className="pointer-events-none absolute rounded-sm border-2 border-yellow-400"
            style={faceBoxStyle(tag, imageRect)}
          >
            <span className="absolute top-full left-1/2 mt-1 -translate-x-1/2 rounded bg-black/70 px-1.5 py-0.5 text-xs whitespace-nowrap text-white">
              {tag.contact_display_name}
            </span>
          </div>
        ))}
      {editing && pendingBox && (
        <>
          <div
            aria-hidden="true"
            data-testid="pending-face-box"
            className="pointer-events-none absolute border-2 border-dashed border-yellow-400"
            style={faceBoxStyle(pendingBox, imageRect)}
          />
          <div
            className="absolute z-10 w-64 -translate-x-1/2 space-y-1 rounded-md border border-border bg-background p-2 shadow-lg"
            style={popoverStyle(pendingBox, imageRect)}
          >
            <TagNameSearch
              label="Search people to tag"
              excludeContactIds={photo.tags.map((tag) => tag.contact_id)}
              disabled={addMutation.isPending}
              onSelect={(option) =>
                addMutation.mutate({
                  media_id: photo.id,
                  contact_id: option.id,
                  x: pendingBox.x,
                  y: pendingBox.y,
                  w: pendingBox.w,
                  h: pendingBox.h,
                })
              }
            />
            <Button
              size="sm"
              variant="ghost"
              className="w-full"
              disabled={addMutation.isPending}
              onClick={() => setPendingBox(null)}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </>
  );
}

/**
 * One tag row in edit mode: the name plus the admin's correction controls —
 * remove the tag, or point it at the right contact. Reaches the member-facing
 * queries as well as the admin list, so viewers see the fix immediately.
 */
function EditableTagRow({
  tag,
  excludeContactIds,
}: {
  tag: MediaContactTag;
  /** Every contact already tagged on the photo — hidden from the reassign search. */
  excludeContactIds: string[];
}) {
  const invalidatePhotoSources = useInvalidatePhotoSources();
  const [reassigning, setReassigning] = useState(false);

  const deleteMutation = trpc.admin.media.deleteTag.useMutation({
    onSuccess: () => {
      toast.success("Tag removed.");
      invalidatePhotoSources();
    },
    onError: (err) => toast.error(err.message),
  });
  const reassignMutation = trpc.admin.media.reassignTag.useMutation({
    onSuccess: (updated) => {
      toast.success(`Tag reassigned to ${updated.contact_display_name}.`);
      setReassigning(false);
      invalidatePhotoSources();
    },
    onError: (err) => toast.error(err.message),
  });

  const saving = deleteMutation.isPending || reassignMutation.isPending;

  return (
    <li className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate">{tag.contact_display_name}</span>
        {saving ? (
          <span className="shrink-0 text-xs text-zinc-400">Saving…</span>
        ) : (
          <>
            <button
              type="button"
              className="shrink-0 text-xs text-yellow-400 hover:underline"
              aria-label={`Reassign tag of ${tag.contact_display_name}`}
              onClick={() => setReassigning((r) => !r)}
            >
              Reassign
            </button>
            <button
              type="button"
              className="shrink-0 text-xs text-red-400 hover:underline"
              aria-label={`Remove tag of ${tag.contact_display_name}`}
              onClick={() => deleteMutation.mutate({ tag_id: tag.id })}
            >
              Remove
            </button>
          </>
        )}
      </div>
      {reassigning && !saving && (
        <TagNameSearch
          label={`Search a contact to replace ${tag.contact_display_name}`}
          excludeContactIds={excludeContactIds}
          onSelect={(option) => reassignMutation.mutate({ tag_id: tag.id, contact_id: option.id })}
        />
      )}
    </li>
  );
}

/**
 * The panel beside the photo: who took it (with a profile link when they have
 * an account), its metadata, the description, and the tagged names — hovering
 * or focusing a name highlights that face on the photo. Always dark to match
 * the lightbox chrome. Admins also get the tag-editing toggle here on
 * surfaces that pass canEditTags.
 */
function InfoPanel({
  photo,
  collapsed,
  onToggle,
  onTagHighlight,
  showTagEditToggle,
  editing,
  onToggleEditing,
  renderPanelExtra,
}: {
  photo: LightboxPhoto;
  collapsed: boolean;
  onToggle: () => void;
  onTagHighlight: (tagId: string | null) => void;
  showTagEditToggle: boolean;
  editing: boolean;
  onToggleEditing: () => void;
  renderPanelExtra?: (photoId: string) => React.ReactNode;
}) {
  if (collapsed) {
    // Collapsed to a rail, contacts-sidebar style: the toggle stays put so
    // expanding is one click in the same spot on every photo.
    return (
      <aside className="flex w-full shrink-0 justify-end bg-zinc-900 p-2 transition-[width] duration-200 ease-in-out md:h-full md:w-12 md:justify-center md:pt-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-zinc-400 hover:bg-white/10 hover:text-white"
          onClick={onToggle}
          aria-label="Expand info panel"
        >
          <PanelRightOpen className="size-4" />
        </Button>
      </aside>
    );
  }

  return (
    <aside className="max-h-[40dvh] w-full shrink-0 space-y-4 overflow-y-auto bg-zinc-900 p-4 text-sm text-zinc-100 transition-[width] duration-200 ease-in-out md:max-h-none md:h-full md:w-80">
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="icon"
          className="-m-2 h-8 w-8 text-zinc-400 hover:bg-white/10 hover:text-white"
          onClick={onToggle}
          aria-label="Collapse info panel"
        >
          <PanelRightClose className="size-4" />
        </Button>
      </div>
      {photo.uploader && (
        <LightboxPanelSection label="Submitted by">
          {photo.uploader.username ? (
            <Link
              to={`/profile/${photo.uploader.username}`}
              className="font-medium text-yellow-400 hover:underline"
            >
              {photo.uploader.display_name}
            </Link>
          ) : (
            <p className="font-medium">{photo.uploader.display_name}</p>
          )}
        </LightboxPanelSection>
      )}

      {!renderPanelExtra && (
        <LightboxPanelSection label="Details">
          <PhotoFacts width={photo.width} height={photo.height} uploadedAt={photo.created_at} />
        </LightboxPanelSection>
      )}

      {photo.description && !renderPanelExtra && (
        <LightboxPanelSection label="Description">
          <p className="whitespace-pre-wrap">{photo.description}</p>
        </LightboxPanelSection>
      )}

      {(photo.tags.length > 0 || editing) && (!renderPanelExtra || showTagEditToggle) && (
        <LightboxPanelSection label="Tagged">
          {editing ? (
            <>
              <p className="mt-1 text-xs text-zinc-400">
                Click a face in the photo, then pick a name.
              </p>
              {photo.tags.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {photo.tags.map((tag) => (
                    <EditableTagRow
                      key={tag.id}
                      tag={tag}
                      excludeContactIds={photo.tags.map((t) => t.contact_id)}
                    />
                  ))}
                </ul>
              )}
            </>
          ) : (
            <ul className="mt-1 space-y-1">
              {photo.tags.map((tag) => (
                <li key={tag.id}>
                  <button
                    type="button"
                    className="rounded-sm text-left hover:text-yellow-400 focus-visible:text-yellow-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-yellow-400"
                    onMouseEnter={() => onTagHighlight(tag.id)}
                    onMouseLeave={() => onTagHighlight(null)}
                    onFocus={() => onTagHighlight(tag.id)}
                    onBlur={() => onTagHighlight(null)}
                  >
                    {tag.contact_display_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </LightboxPanelSection>
      )}

      {showTagEditToggle && (
        <Button
          size="sm"
          variant="outline"
          className="border-zinc-600 bg-transparent text-zinc-100 hover:bg-white/10 hover:text-white"
          onClick={onToggleEditing}
        >
          {editing ? "Done editing tags" : "Edit tags"}
        </Button>
      )}

      {renderPanelExtra?.(photo.id)}
    </aside>
  );
}

/**
 * Full-screen viewer for one event gallery. Stepping wraps in both directions
 * so a member can keep going round a set of holiday photos without hitting a
 * dead end at either edge.
 */
export function GalleryLightbox({
  photos,
  index,
  onIndexChange,
  canEditTags = false,
  renderPanelExtra,
}: GalleryLightboxProps) {
  const open = index !== null;
  const count = photos.length;
  // Admin-or-webmaster, matching the server's adminProcedure gate. Non-admins
  // never see the tag editor, whatever the surface passes for canEditTags.
  const { isAdmin } = useAuth();
  // Open by default each time the lightbox opens; hiding it applies to every
  // photo for the rest of the session (the component unmounts on close).
  const [panelOpen, setPanelOpen] = useState(true);
  // The tag whose face box is showing — set while its name is hovered or
  // focused in the panel, and dropped when the member steps to another photo.
  const [activeTagId, setActiveTagId] = useState<string | null>(null);
  // Tag-edit mode, armed per photo: stepping away drops back to viewing.
  const [editingTags, setEditingTags] = useState(false);

  useEffect(() => {
    setActiveTagId(null);
    setEditingTags(false);
  }, [index]);

  const showTagEditToggle = canEditTags && isAdmin;
  const editing = showTagEditToggle && editingTags;

  const step = useCallback(
    (delta: number) => {
      if (index === null || count === 0) return;
      onIndexChange((index + delta + count) % count);
    },
    [index, count, onIndexChange],
  );

  // Bound to the window rather than the dialog: Radix moves focus around
  // inside the content, so a handler on any one element would go deaf as soon
  // as the member clicked a stepping button.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      // Typing in the tag editor's search box must not step photos.
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
      ) {
        return;
      }
      if (event.key === "ArrowRight") step(1);
      else if (event.key === "ArrowLeft") step(-1);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, step]);

  if (index === null) return null;
  const photo = photos[index];
  if (!photo) return null;

  const activeTag = photo.tags.find((tag) => tag.id === activeTagId) ?? null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onIndexChange(null)}>
      <DialogContent
        className="h-[100dvh] max-h-none w-screen max-w-none sm:max-w-none gap-0 rounded-none sm:rounded-none border-0 bg-black/95 p-0 shadow-none overflow-hidden"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Event photo</DialogTitle>
        <div className="flex h-full min-h-0 flex-col md:flex-row">
          <div className="relative min-h-0 min-w-0 flex-1">
            <LightboxImage photo={photo} activeTag={activeTag} editing={editing} />

            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 text-white hover:bg-white/20 hover:text-white"
              onClick={() => onIndexChange(null)}
              aria-label="Close"
            >
              <X className="size-5" />
            </Button>

            {count > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-1/2 left-2 -translate-y-1/2 text-white hover:bg-white/20 hover:text-white"
                  onClick={() => step(-1)}
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="size-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-1/2 right-2 -translate-y-1/2 text-white hover:bg-white/20 hover:text-white"
                  onClick={() => step(1)}
                  aria-label="Next photo"
                >
                  <ChevronRight className="size-6" />
                </Button>
                <p className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white">
                  {index + 1} / {count}
                </p>
              </>
            )}
          </div>

          <InfoPanel
            photo={photo}
            collapsed={!panelOpen}
            onToggle={() => setPanelOpen((o) => !o)}
            onTagHighlight={setActiveTagId}
            showTagEditToggle={showTagEditToggle}
            editing={editing}
            onToggleEditing={() => setEditingTags((e) => !e)}
            renderPanelExtra={renderPanelExtra}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
